/**
 * מטמון סטטיסטיקות דשבורד — זיכרון (TTL קצר) + sessionStorage (תצוגה מיידית בכניסה / רענון).
 */
export const DASHBOARD_STATS_TTL_MS = 2 * 60 * 1000;
/** מקסימום גיל למטמון תצוגה בסשן — stale-while-revalidate */
export const DASHBOARD_STATS_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type DashboardStatsSnapshot = {
  customers: number;
  newCustomersThisMonth: number;
  openRepairs: number;
  monthlyRevenue: number;
  weeklyRepairs: number;
  receivedToday: number;
  receivedThisWeek: number;
  receivedThisMonth: number;
  weeklyRevenue: number;
  dailyRevenue: number;
  openRepairsAll: number;
  openRepairsMy: number;
  repairedThisWeek: number;
  repairedToday: number;
  repairedThisMonth: number;
  readyToReturn: number;
  repairsInLab: number;
  repairsInTreatment: number;
  repairsWaitingForApproval: number;
  repairsCompleted: number;
  repairsWaitingToReturn: number;
  repairsReturnedToCustomer: number;
  averagePerPaidCustomer: number;
  paidRepairsThisMonth: number;
  freeRepairsThisMonth: number;
  technicianStats: Array<{
    name: string;
    today: number;
    week: number;
    month: number;
    todayRevenue: number;
    weekRevenue: number;
    monthRevenue: number;
  }>;
  serviceStats: Array<{
    name: string;
    today: number;
    week: number;
    month: number;
    todayRevenue: number;
    weekRevenue: number;
    monthRevenue: number;
    todayCalls: number;
    weekCalls: number;
    monthCalls: number;
  }>;
  nonCreditServiceStats: Array<{
    name: string;
    today: number;
    week: number;
    month: number;
    todayRevenue: number;
    weekRevenue: number;
    monthRevenue: number;
    todayCalls: number;
    weekCalls: number;
    monthCalls: number;
  }>;
  todayCollection: { cash: number; credit: number; transfer: number; invoice: number };
  repairsOlderThan7Days: number;
};

export type DashboardStatsCachePayload = {
  stats: DashboardStatsSnapshot;
  currentEmployeeId: string;
};

export type DashboardStatsSessionMeta = {
  summaryRefreshedAt: string | null;
  summaryVersion: number;
};

type MemoryEntry = {
  cacheKey: string;
  expiresAt: number;
  payload: DashboardStatsCachePayload;
  meta: DashboardStatsSessionMeta;
};

type SessionBlob = {
  cacheKey: string;
  cachedAt: number;
  payload: DashboardStatsCachePayload;
  meta: DashboardStatsSessionMeta;
};

const SESSION_STORAGE_KEY = 'dashboard:stats-cache:v2';

let memoryEntry: MemoryEntry | null = null;
let latestDashboardSummaryVersion = 0;

function readSessionBlob(): SessionBlob | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionBlob;
    if (!parsed?.payload?.stats || typeof parsed.cacheKey !== 'string') return null;
    const cachedAt = Number(parsed.cachedAt || 0);
    if (!Number.isFinite(cachedAt) || cachedAt <= 0) return null;
    if (Date.now() - cachedAt > DASHBOARD_STATS_SESSION_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionBlob(blob: SessionBlob): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(blob));
  } catch {
    /* quota / private mode */
  }
}

function uidFromCacheKey(cacheKey: string): string {
  const parts = String(cacheKey || '').split(':');
  return parts.length > 1 ? parts[1] : '';
}

function sessionBlobForDisplay(cacheKey: string): SessionBlob | null {
  const blob = readSessionBlob();
  if (!blob) return null;
  if (blob.cacheKey === cacheKey) return blob;
  const wantedUid = uidFromCacheKey(cacheKey);
  const blobUid = uidFromCacheKey(blob.cacheKey);
  if (wantedUid && blobUid && wantedUid === blobUid) return blob;
  return null;
}

function hasMeaningfulStats(stats: DashboardStatsSnapshot): boolean {
  const scalarSum =
    Number(stats.repairsInLab || 0) +
    Number(stats.repairsInTreatment || 0) +
    Number(stats.repairsWaitingForApproval || 0) +
    Number(stats.repairsCompleted || 0) +
    Number(stats.repairsWaitingToReturn || 0) +
    Number(stats.monthlyRevenue || 0) +
    Number(stats.customers || 0);
  if (scalarSum > 0) return true;
  return (
    (Array.isArray(stats.technicianStats) && stats.technicianStats.length > 0) ||
    (Array.isArray(stats.serviceStats) && stats.serviceStats.length > 0)
  );
}

export function readDashboardStatsCache(
  cacheKey: string,
  options?: { allowStale?: boolean },
): DashboardStatsCachePayload | null {
  const allowStale = Boolean(options?.allowStale);
  const now = Date.now();
  if (
    memoryEntry &&
    memoryEntry.cacheKey === cacheKey &&
    (memoryEntry.expiresAt > now || allowStale)
  ) {
    return memoryEntry.payload;
  }
  const session = sessionBlobForDisplay(cacheKey);
  if (session && (allowStale || session.cachedAt + DASHBOARD_STATS_TTL_MS > now)) {
    return session.payload;
  }
  return null;
}

export function readDashboardStatsSessionMeta(
  cacheKey: string,
): DashboardStatsSessionMeta | null {
  const session = sessionBlobForDisplay(cacheKey);
  return session?.meta ?? null;
}

/** טעינה מיידית לפני רשת — מחזיר מטמון סשן גם אם פג תוקף זיכרון */
export function readDashboardStatsStaleDisplay(
  cacheKey: string,
): { payload: DashboardStatsCachePayload; meta: DashboardStatsSessionMeta } | null {
  const fresh = readDashboardStatsCache(cacheKey, { allowStale: true });
  const session = sessionBlobForDisplay(cacheKey);
  if (fresh && session && hasMeaningfulStats(fresh.stats)) {
    return { payload: fresh, meta: session.meta };
  }
  if (session && hasMeaningfulStats(session.payload.stats)) {
    return { payload: session.payload, meta: session.meta };
  }
  if (fresh && hasMeaningfulStats(fresh.stats)) {
    return {
      payload: fresh,
      meta: session?.meta ?? { summaryRefreshedAt: null, summaryVersion: 0 },
    };
  }
  return null;
}

export function writeDashboardStatsCache(
  cacheKey: string,
  payload: DashboardStatsCachePayload,
  meta?: Partial<DashboardStatsSessionMeta>,
): void {
  const mergedMeta: DashboardStatsSessionMeta = {
    summaryRefreshedAt: meta?.summaryRefreshedAt ?? null,
    summaryVersion: Number(meta?.summaryVersion ?? latestDashboardSummaryVersion) || 0,
  };
  memoryEntry = {
    cacheKey,
    expiresAt: Date.now() + DASHBOARD_STATS_TTL_MS,
    payload,
    meta: mergedMeta,
  };
  writeSessionBlob({
    cacheKey,
    cachedAt: Date.now(),
    payload,
    meta: mergedMeta,
  });
}

/** מבטל מטמון זיכרון בלבד — session נשאר לתצוגה עד שהרשת מעדכנת */
export function invalidateDashboardStatsCache(): void {
  memoryEntry = null;
}

export function clearDashboardStatsSessionCache(): void {
  memoryEntry = null;
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function getDashboardSummaryVersionCache(): number {
  return latestDashboardSummaryVersion;
}

export function setDashboardSummaryVersionCache(version: number): void {
  latestDashboardSummaryVersion = Number(version || 0);
}
